import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

// Components
import Search from '../Search';
import SearchResults from '../Search/SearchResults';
import SearchRefinementsProperty from '../../_organisms/SearchRefinementsProperty';
import PropertyCardBase from '@molecules/PropertyCard';
import PropertyListItemBase from '@molecules/PropertyListItem';

// Functionality
import { getUnixTimestamp } from '@general/dates';
import { SAVED_PROPERTIES_QUERY } from '@general/graphql/properties.graph';
import { mapSearchPropertyToProps } from '@general/transforms/property.transform';
import { generateOptionalFiltersFromSavedSearch } from '../../../hooks/algoliaFilters/useMemberMatchFilters';
import SavedSearchesSelector from './SavedSearchesSelector';
import useViewer from '@hooks/useViewer';

const PropertyCard = props => <PropertyCardBase openInNewTab {...props} />;
const PropertyListItem = props => <PropertyListItemBase openInNewTab {...props} />;

const getFindPropertiesFilters = (viewer, additionalFilters) => {
    let filterString = `expiresAtTimestamp >= ${getUnixTimestamp()}`;
    filterString += ' AND manager.status:Active';

    if (viewer?.recordId) {
        filterString += ` AND NOT manager.recordId:"${viewer.recordId}"`;
    }

    if (additionalFilters) {
        filterString += ` AND ${additionalFilters}`;
    }

    return filterString;
};

const mapFiltersToRefinements = filters =>
    filters.reduce((group, filterGroup) => {
        let attribute;

        if (typeof filterGroup === 'string') return group;

        const values = filterGroup.map(filter => {
            const [namespace, value] = filter.split(':');
            attribute = namespace.split('.')[2];
            return value;
        });

        return {
            ...group,
            [attribute]: values,
        };
    }, {});

const SearchPropertiesActive = ({
    defaultViewType,
    displayMap,
    updateSearchStateUrl,
    initialSearchState,
    additionalFilters,
}) => {
    const { viewer } = useViewer();

    const onSearchSelect = useCallback(
        search => {
            const filters = generateOptionalFiltersFromSavedSearch(search);
            if (!filters) return;
            const refinements = mapFiltersToRefinements(filters);

            setTimeout(() => {
                if (purposeRef.current.attribute && refinements.purpose) {
                    purposeRef.current.refine(refinements.purpose);
                }
                if (densityRef.current.attribute && refinements.density) {
                    densityRef.current.refine(refinements.density);
                }
                if (typeOfCenterRef.current.attribute && refinements.typeOfCenter) {
                    typeOfCenterRef.current.refine(refinements.typeOfCenter);
                }
                if (sizeRef.current.attribute && refinements.size) {
                    sizeRef.current.refine(refinements.size);
                }
                if (typeOfFacilityRef.current.attribute && refinements.typeOfFacility) {
                    typeOfFacilityRef.current.refine(refinements.typeOfFacility);
                }
            }, 0);
        },
        []
    );

    const purposeRef = useRef({ attribute: null, refine: null });
    const densityRef = useRef({ attribute: null, refine: null });
    const typeOfCenterRef = useRef({ attribute: null, refine: null });
    const sizeRef = useRef({ attribute: null, refine: null });
    const typeOfFacilityRef = useRef({ attribute: null, refine: null });

    return (
        <Search
            index="properties"
            filters={getFindPropertiesFilters(viewer, additionalFilters)}
            refinementComponent={() => (
                <SearchRefinementsProperty
                    purposeRef={purposeRef}
                    densityRef={densityRef}
                    typeOfCenterRef={typeOfCenterRef}
                    sizeRef={sizeRef}
                    typeOfFacilityRef={typeOfFacilityRef}
                />
            )}
            defaultViewType={defaultViewType}
            initialSearchState={initialSearchState}
            displayMap={displayMap}
            updateSearchStateUrl={updateSearchStateUrl}
            AboveSearchBoxComponent={() => <SavedSearchesSelector onSearchSelect={onSearchSelect} />}
        >
            <SearchResults
                cardComponent={props => <PropertyCard showShareIcon {...props} />}
                index="properties"
                listItemComponent={PropertyListItem}
                parentView="Properties"
                parentSubview="Find Properties"
                query={SAVED_PROPERTIES_QUERY}
                transformFunc={mapSearchPropertyToProps}
                typePlural="savedProperties"
            />
        </Search>
    );
};

SearchPropertiesActive.propTypes = {
    defaultViewType: PropTypes.string,
    displayMap: PropTypes.bool,
    updateSearchStateUrl: PropTypes.bool,
    additionalFilters: PropTypes.string,
    initialSearchState: PropTypes.object,
};

SearchPropertiesActive.defaultProps = {
    defaultViewType: 'card',
    displayMap: false,
    updateSearchStateUrl: false,
    initialSearchState: {},
    additionalFilters: '',
};

export default SearchPropertiesActive;